import { useQuery } from '@tanstack/react-query';
import { getActiveCodelist, listStandardCodes } from '../../api/endpoints';
import { ApiError } from '../../api/http';
import { queryKeys } from '../../api/queryKeys';
import type { MappingKind } from '../../api/types';
import type { CatalogueCode } from './targetOptions';

export interface TargetCatalogue {
  /** Codes to offer; empty while loading or when both sources failed. */
  codes: CatalogueCode[];
  /** Label of the active version the codes come from; null when the built-in selection is shown. */
  versionLabel: string | null;
  isPending: boolean;
}

/**
 * The codes the target picker offers: the tenant's active code list of the kind when one has been
 * activated, otherwise the library's built-in selection. A 404 from the reader is the normal
 * "nothing activated yet" answer, not a failure — so it is read as null, and the built-in list is
 * fetched only then (or when the reader fails outright, so the picker never goes empty for a
 * reason the user cannot fix). The lists are shared by every tenant, so nothing waits for one.
 */
export function useTargetCatalogue(kind: MappingKind): TargetCatalogue {
  const active = useQuery({
    queryKey: queryKeys.codelists.active(kind),
    queryFn: async ({ signal }) => {
      try {
        return await getActiveCodelist(kind, signal);
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    },
  });

  const builtIn = useQuery({
    queryKey: queryKeys.standardCodes.byKind(kind),
    queryFn: ({ signal }) => listStandardCodes(kind, signal),
    staleTime: Infinity,
    enabled: active.isSuccess ? active.data === null : active.isError,
  });

  if (active.isPending) return { codes: [], versionLabel: null, isPending: true };
  if (active.data) {
    return {
      codes: active.data.entries.map((entry) => ({ code: entry.code, description: entry.name })),
      versionLabel: active.data.versionLabel,
      isPending: false,
    };
  }
  return { codes: builtIn.data ?? [], versionLabel: null, isPending: builtIn.isPending && builtIn.isEnabled };
}
